'use client';
/* eslint-disable @typescript-eslint/no-unused-vars */
import classNames from 'classnames';
import {useI18n} from '@udemy-v2/i18n';
import {useEffect, useMemo} from 'react';
import {CourseVariant, CourseVariation, CourseWithVariationAggregation} from '@/types/course';
import {AsyncStaticPriceText, useCoursePriceStore} from '@udemy-v2/react-course-pricing';
import {
    PriceEventTrackingProvider,
    PURCHASE_PRICE_TYPES,
} from '@udemy-v2/react-currency-formatting';
import {CourseVariationOption, CourseVariationType} from '@udemy-v2/graphql';
import {Icon} from '@udemy-v2/icons';
import {ShowMoreRows} from './show-more-rows.react-component';
import {Button, ButtonProps} from '@udemy-v2/react-core-components';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';
import {
    getCourseVariationsCombinations,
    VALUE_DELIMITER,
    variantSortHelper,
    VariationCombination,
} from './course-variations-utils';

import styles from './course-variations-overview.module.scss';
import {
    VariationGroupPresented,
    VariationGroupItemSelected,
} from '@/features/course-landing-page/events/variations-events';

type VariationOption = CourseVariation['variants'][number]['option'];
import {useViewport, withinOrWiderThan} from '@udemy-v2/react-layout-components';

/**
 * Props for the CourseVariationsOverview component
 */
export interface CourseVariationsOverviewProps {
    titleTypography?: 'ud-heading-lg' | 'ud-heading-xl';
    course: CourseWithVariationAggregation;
}

const ShowMoreButton = (props: ButtonProps) => (
    <Button {...props} udStyle="ghost">
        {props.children}
    </Button>
);

/**
 * Component that displays an overview of course variations
 * This allows learners to see and compare different versions of a course
 * (e.g., different languages, editions, or audience-specific variants)
 *
 * @component
 *
 */
export function CourseVariationsOverview({
    course,
    titleTypography = 'ud-heading-xl',
}: CourseVariationsOverviewProps): React.ReactNode {
    const {gettext} = useI18n();
    const {range} = useViewport();
    const isWideViewport = range && withinOrWiderThan(range, 'xl');
    const coursePriceStore = useCoursePriceStore();

    const variations = useMemo(
        () => course.variationAggregation?.variations || [],
        [course.variationAggregation?.variations],
    );

    const combinationsOfVariations = useMemo(() => {
        return getCourseVariationsCombinations(variations);
    }, [variations]);

    const trackVariationGroupImpression = () => {
        publishEvent(
            new VariationGroupPresented({
                courseId: Number(course.id),
                variationGroupId: Number(course.variationAggregation?.id),
            }),
        );
    };

    const trackIncentiveClick = async (clickedCourseId?: number) => {
        publishEvent(
            new VariationGroupItemSelected({
                courseId: Number(course.id),
                selectedVariantCourseId: Number(clickedCourseId),
                variationGroupId: Number(course.variationAggregation?.id),
            }),
        );
    };

    const selectedVariationCombination = useMemo(() => {
        return Array.from(combinationsOfVariations.values())
            .values()
            .find((combination) => combination.course.id === course.id);
    }, [combinationsOfVariations, course.id]);

    useEffect(() => {
        variations.forEach((variation) => {
            variation.variants.forEach((variant) => {
                coursePriceStore.registerCourseById(Number(variant.course.id));
            });
        });
    }, [variations, coursePriceStore]);

    if (variations.length === 0) {
        return null;
    }

    const calculateKey = (levelIndex: number, type: CourseVariationType, optionId: string) => {
        const selectedParts = selectedVariationCombination?.id.split(VALUE_DELIMITER) ?? [];

        // If the selected parts are less than or equal to 2 that means we have only one variation, return the key for the current variation
        if (selectedParts.length <= 2) {
            return `${type}${VALUE_DELIMITER}${optionId.trim()}`;
        } else if (levelIndex === 0) {
            // If the level index is 0 that means we are in the first variation, return the key with the second and third parts of the selected parts
            return `${type}${VALUE_DELIMITER}${optionId.trim()}${VALUE_DELIMITER}${selectedParts[2]}${VALUE_DELIMITER}${selectedParts[3]}`;
        } else {
            // If the level index is not 0 that means we are in the second variation, return the key with the first and second parts of the selected parts and the current type and option id
            // We never have more than 2 variations, so we can safely assume that the first and second parts of the selected parts are the first and second parts of the selected parts
            return `${selectedParts[0]}${VALUE_DELIMITER}${selectedParts[1]}${VALUE_DELIMITER}${type}${VALUE_DELIMITER}${optionId.trim()}`;
        }
    };

    const renderVariantCategoryItem = (
        option: CourseVariationOption,
        levelIndex: number,
        type: CourseVariationType,
    ) => {
        const isSelected = selectedVariationCombination?.id.includes(
            `${type}${VALUE_DELIMITER}${option.id.trim()}`,
        );

        const key = calculateKey(levelIndex, type, option.id);
        const courseVariant = combinationsOfVariations?.get(key);

        // If the course variant is not found for this combination, return null
        // This happens when variant for particular combination is not present which is valid scenario
        if (!courseVariant) {
            return null;
        }

        if (!isSelected) {
            return getUnselectedVariantPill(option, courseVariant);
        }

        return getSelectedVariantPill(option, courseVariant);
    };

    const getSelectedVariantPill = (
        option: CourseVariationOption,
        courseVariant: VariationCombination,
    ) => {
        return (
            <div
                className={classNames(styles['category-item'], styles['category-item-selected'])}
                key={option.id}
            >
                <div
                    className={classNames('ud-text-sm', styles['category-item-title'])}
                    title={option.label}
                >
                    {option.label}
                </div>
                <div className={styles['filler']}></div>
                <div className={styles['category-item-content']}>
                    <div className={classNames('ud-text-sm', 'ud-text-bold')}>
                        {getPriceText(Number(courseVariant?.course.id))}
                    </div>
                    <Icon glyph="tick" size="medium" label={false} />
                </div>
            </div>
        );
    };

    const getUnselectedVariantPill = (
        option: CourseVariationOption,
        courseVariant: VariationCombination,
    ) => {
        return (
            <div className={classNames(styles['category-item'])} key={option.id}>
                <a
                    className={classNames(styles['category-item-link'])}
                    href={`${courseVariant?.course.urlCourseLanding}`}
                    onClick={() => trackIncentiveClick(Number(courseVariant.course.id))}
                    target={isWideViewport ? '_blank' : '_self'}
                >
                    <div className={styles['category-item-title']} title={option.label}>
                        {option.label}
                    </div>
                    <div className={styles['filler']}></div>
                    <div className={styles['category-item-content']}>
                        <div className={classNames('ud-text-sm', 'ud-text-bold')}>
                            {getPriceText(Number(courseVariant.course.id))}
                        </div>
                        {isWideViewport && <Icon size="medium" glyph="open-in-new" label={false} />}
                    </div>
                </a>
            </div>
        );
    };

    const getPriceText = (courseId: number) => {
        return (
            <PriceEventTrackingProvider
                priceEventTrackingContext={{
                    buyableTrackingId: course.trackingId,
                    isBundleDiscountEnabled: false,
                    priceType: PURCHASE_PRICE_TYPES.individual_buyable,
                }}
            >
                <AsyncStaticPriceText courseId={courseId} stackedLayout={false} />
            </PriceEventTrackingProvider>
        );
    };

    const getCategoryUniqueValues = (variation: CourseVariation) => {
        // Get unique options in a particular variation.
        const uniqueOptionsMap = new Map<string, CourseVariant>();
        variation.variants.forEach((variant) => {
            const optionId = variant.option.id;
            if (!uniqueOptionsMap.has(optionId)) {
                uniqueOptionsMap.set(optionId, variant);
            }
        });
        return Array.from(uniqueOptionsMap.values())
            .sort((a, b) => variantSortHelper(a, b, course.id))
            .map((variant) => variant.option);
    };

    return (
        <>
            <span className="in-page-offset-anchor" id="course-variations" />
            <TrackImpression trackFunc={trackVariationGroupImpression}>
                <div className={classNames('component-margin', styles['course-variations'])}>
                    <div className={styles['course-variations-content']}>
                        <div className={styles['course-variations-header']}>
                            <h2 className={classNames(titleTypography, styles['title'])}>
                                {gettext('Get started with the right edition for you')}
                            </h2>
                            <div className="ud-text-sm">
                                {gettext('Choose what fits for your goals and learning style')}
                            </div>
                        </div>
                        <div className={styles['course-variations-categories']}>
                            {variations.map((variation, levelIndex) => (
                                <div
                                    className={styles['category-container']}
                                    key={`${variation.type}-${levelIndex}`}
                                >
                                    <div
                                        className={classNames(
                                            'ud-heading-md',
                                            styles['category-title'],
                                        )}
                                    >
                                        {variation.name.toLocaleLowerCase()}:
                                    </div>

                                    <ShowMoreRows
                                        rowsToShow={isWideViewport ? 1 : 3}
                                        countPerRow={isWideViewport ? 3 : 1}
                                        ButtonComponent={ShowMoreButton}
                                        data-purpose="show-more-container"
                                    >
                                        {getCategoryUniqueValues(variation).map((level) =>
                                            renderVariantCategoryItem(
                                                level,
                                                levelIndex,
                                                variation.type,
                                            ),
                                        )}
                                    </ShowMoreRows>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </TrackImpression>
        </>
    );
}
